import type { ReactNode } from 'react'
import { Fader } from './Fader.js'
import { channelIsInvalid, type BehaviorChannelSetting, type ScoredInput } from './simulatorModel.js'

const SEGMENTS = 12
const EXCLUSION_TEXT = { switched_off: 'Off', no_weight: 'No weight', no_data: 'No data' } as const

/** A column of lamps showing a 0-100 value, lit from the bottom: coral low, gold middle, green high. */
function LedMeter({ value }: { value: number | null }) {
  const lit = value === null ? 0 : Math.round((value / 100) * SEGMENTS)
  return (
    <div aria-hidden="true" className="flex h-[132px] w-2.5 flex-col-reverse gap-[2px]">
      {Array.from({ length: SEGMENTS }, (_, index) => {
        const color = index < SEGMENTS / 3 ? 'bg-coral' : index < (SEGMENTS * 2) / 3 ? 'bg-gold' : 'bg-green'
        return <span key={index} className={`flex-1 rounded-[1px] ${index < lit ? `${color} shadow-[0_0_4px_currentColor]` : 'bg-white/10'}`} />
      })}
    </div>
  )
}

const asPercent = (fraction: number): string => `${Math.round(fraction * 100)}%`

interface BehaviorChannelProps {
  label: string
  /** Small print under the name: what the channel measures. */
  caption: string
  setting: BehaviorChannelSetting
  onChange: (setting: BehaviorChannelSetting) => void
  /** How this channel came out in the latest calculation. */
  result: ScoredInput | undefined
  /** `rate`: anchors are fractions shown as percentages. `count`: anchors are whole occurrences. */
  anchorUnit: 'rate' | 'count'
  /** A minimum per period: one fader for the minimum (the `best` anchor), none for `worst`, which the engine does not read. */
  minimumOnly?: boolean
  /** An extra fader ahead of the others - a custom channel's occurrence count. */
  leading?: ReactNode
  onRemove?: () => void
}

/**
 * One behavior as a channel strip: an on/off lamp, a meter for the value it
 * came out at, and faders for its weight and its two anchors - the level
 * that earns 100 and the level that earns 0.
 */
export function BehaviorChannel({ label, caption, setting, onChange, result, anchorUnit, minimumOnly = false, leading, onRemove }: BehaviorChannelProps) {
  const isRate = anchorUnit === 'rate'
  const anchorFader = (name: string, field: 'best' | 'worst') => (
    <Fader
      label={name}
      group={label}
      value={isRate ? Math.round(setting[field] * 100) : setting[field]}
      min={0}
      max={isRate ? 100 : 20}
      disabled={!setting.enabled}
      format={(value) => (isRate ? `${value}%` : String(value))}
      onChange={(value) => onChange({ ...setting, [field]: isRate ? value / 100 : value })}
    />
  )
  const hasClash = channelIsInvalid(minimumOnly ? 'per_period_minimum' : 'per_period', setting)

  return (
    <div className={`shrink-0 rounded border p-3 ${setting.enabled ? 'border-white/15 bg-white/[0.04]' : 'border-white/10 bg-black/20'}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs font-black uppercase tracking-wide ${setting.enabled ? 'text-white' : 'text-white/45'}`}>{label}</div>
          <div className="text-[10px] text-white/45">{caption}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={setting.enabled}
          aria-label={`Measure ${label}`}
          onClick={() => onChange({ ...setting, enabled: !setting.enabled })}
          className={`shrink-0 rounded-sm border px-2 py-1 text-[10px] font-black tracking-widest transition-colors ${
            setting.enabled ? 'border-green bg-green/20 text-green shadow-[0_0_8px_rgba(46,139,87,0.6)]' : 'border-white/25 text-white/40'
          }`}
        >
          {setting.enabled ? 'ON' : 'OFF'}
        </button>
      </div>

      <div className="flex items-start gap-3">
        <LedMeter value={result?.value ?? null} />
        {leading}
        <Fader label="Weight" group={label} value={setting.weight} min={0} max={100} disabled={!setting.enabled} onChange={(weight) => onChange({ ...setting, weight })} />
        {minimumOnly ? (
          <Fader label="Minimum" group={label} value={setting.best} min={1} max={20} disabled={!setting.enabled} format={String} onChange={(best) => onChange({ ...setting, best })} />
        ) : (
          <>
            {anchorFader('100 at', 'best')}
            {anchorFader('0 at', 'worst')}
          </>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-white/10 pt-2 font-mono text-[10px]">
        {hasClash ? (
          <span className="font-bold text-coral">{minimumOnly ? 'The minimum must be a whole number of 1 or more' : '100 and 0 need different levels'}</span>
        ) : result?.included ? (
          <>
            <span className="text-white/60">
              {result.denominator === null ? `${result.numerator}` : isRate && result.measure !== null ? `${asPercent(result.measure)}` : ''}
              {' → '}
              <span className="font-bold text-white">{result.value?.toFixed(1)}</span>
            </span>
            <span className="font-bold text-coral">−{result.pointsLost?.toFixed(1)} pts</span>
          </>
        ) : (
          <span className="text-white/45">{result?.excludedReason ? EXCLUSION_TEXT[result.excludedReason] : '—'}: not counted</span>
        )}
        {onRemove && (
          <button type="button" onClick={onRemove} className="font-sans font-bold text-white/50 hover:text-coral">
            Remove
          </button>
        )}
      </div>
    </div>
  )
}
